/**
 * ReviewDecisionBar — LOCKED decision hierarchy for Milestone 37.3.
 *
 * Approve is the single visually dominant CTA (handled by MissionShell
 * primary). Return is the visible secondary. Archive lives inside a
 * discreet "More actions" overflow — exceptional, never competing with
 * the mission.
 *
 * This component renders ONLY the secondary + overflow. The primary
 * Approve CTA is owned by MissionShell so keyboard focus lands there
 * on mount.
 */
import { useEffect, useRef, useState } from "react";

export function ReviewDecisionBarSecondary({
  onReturn,
  onArchive,
  onRegenerate,
  disabled,
}: {
  onReturn: () => void;
  onArchive: () => void;
  /** Patch 37.4.21 — Optional; shown only when the current asset is a
   * rendered deliverable in ``needs_review``. */
  onRegenerate?: () => void;
  disabled: boolean;
}): JSX.Element {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close overflow menu on outside click / Escape.
  useEffect(() => {
    if (!menuOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  return (
    <div className="flex items-center gap-2" data-testid="review-decision-secondary-group">
      <button
        type="button"
        onClick={onReturn}
        disabled={disabled}
        className="rounded-full border border-white/15 bg-transparent px-4 py-2 text-sm font-medium text-white/85 hover:bg-white/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400 disabled:opacity-50 transition"
        data-testid="review-return-button"
        aria-label="Return for revision"
      >
        Return for Revision
      </button>
      {onRegenerate ? (
        <button
          type="button"
          onClick={onRegenerate}
          disabled={disabled}
          className="rounded-full border border-cyan-400/40 bg-cyan-400/10 px-4 py-2 text-sm font-medium text-cyan-100 hover:bg-cyan-400/15 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400 disabled:opacity-50 transition"
          data-testid="review-regenerate-button"
          aria-label="Regenerate this rendered media"
        >
          Regenerate
        </button>
      ) : null}
      <div className="relative" ref={menuRef}>
        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          disabled={disabled}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          aria-label="More actions"
          className="rounded-full border border-white/10 bg-transparent px-3 py-2 text-sm text-white/60 hover:bg-white/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400 disabled:opacity-40 transition"
          data-testid="review-more-actions-button"
        >
          More
        </button>
        {menuOpen && (
          <div
            role="menu"
            className="absolute bottom-full right-0 mb-2 min-w-[180px] rounded-xl border border-white/10 bg-[color:var(--brand-charcoal,#12151C)] p-1 shadow-xl"
            data-testid="review-more-actions-menu"
          >
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setMenuOpen(false);
                onArchive();
              }}
              className="block w-full rounded-lg px-3 py-2 text-left text-sm text-white/85 hover:bg-white/5"
              data-testid="review-archive-menuitem"
            >
              Archive
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
